import getChatQuestions from "./ChatQuestions";
import {
    setGPTDeparture,
    setGPTLeaveTime, setGPTNightCount,
    setInitialChat,
    setListingGuid, setPriceRangeValue, setStarRating,
    setTravelDate
} from "../../redux/slices/searchFilterSlice";
import { updateQuestionState } from "../../redux/slices/chatBotSlice";
import { GetSearchOnlyHotelsService } from "../../services/HotelServices";
import { toast } from "react-toastify";
import {
    setActivityIndicator,
    setChatGPTOffers,
    setIsChatGPTOffers, setMetaData,
    setMetaDataCount,
    setMetaDataCountByGuid,
    setPriceExceedOffersByGuid,
    setOffers
} from "../../redux/slices/appconfigSlice";
import moment from "moment/moment";
import "moment/locale/nl";
import {
    getAirportFullObject,
    getDesireDestinationFullObject,
    getPagerMiddlePage,
    nightDifferenceCountForFlightsDate
} from "../../helpers/common";
import Swal from "sweetalert2";
import AppConfig from "../../helpers/config";
import GeniusImage from "../../assets/images/genius-bird.png";
import { setLastSearch } from "../../redux/slices/loginconfigSlice";

// Every place that reads a prefilled departure date accepts the same set of shapes:
// "DD MMMM YYYY" in the active locale, the bare "DD MMMM" variant, the US ordering,
// and raw ISO from the recent-search extraction.
export const LEAVE_TIME_FORMATS = ["DD MMMM YYYY", "DD MMMM", "MMMM DD YYYY", "MMMM DD", moment.ISO_8601];

// Parse a stored leaveTime. The caller's locale is tried first, then English, then Dutch,
// because a value written in one session can be read back in another after a language switch
// and Dutch month names only parse under `nl`. Returns null when nothing parses.
export const parseLeaveTime = (leaveTime, momentLocale = 'en') => {
    if (!leaveTime) return null;
    for (const locale of [momentLocale, 'en', 'nl']) {
        const parsed = moment(leaveTime, LEAVE_TIME_FORMATS, locale);
        if (parsed.isValid()) return parsed;
    }
    return null;
};

// `searchfilters` is persisted, so gptFilters.leaveTime survives across sessions and can
// point at a day that has already passed. Confirming such a date is impossible for the user
// (the picker's minDate is today) and useless for the search, so the "still leaving on X?"
// prompt must be skipped and the date picker asked for instead. Unparseable values are left
// alone — they keep the previous behaviour rather than silently losing the answer.
export const isLeaveTimeInPast = (leaveTime, momentLocale = 'en') => {
    const parsed = parseLeaveTime(leaveTime, momentLocale);
    return !!parsed && parsed.isBefore(moment().startOf('day'), 'day');
};

// True only when the stored date is usable as a prefilled "Yes/No" answer.
export const hasUsableLeaveTime = (leaveTime, momentLocale = 'en') =>
    !!leaveTime && !isLeaveTimeInPast(leaveTime, momentLocale);

// True while the date question is waiting on its picker. Only the newest bubble counts
// (chats are newest-first): a recent-search chip sent from this step goes through the
// direct path, which leaves `datePicker` set, and a reply without offers keeps
// `initialChat` on, so looking further back would lock the text input for good.
export const isAwaitingDateSelection = (chats, initialChat) =>
    !!initialChat && !!chats?.[0]?.datePicker;

export const initialQuestionState = (T, gptFilters = null) => ([
    {
        text: getChatQuestions({ T, index: 7 }),
        time: new Date(),
        clientSender: false,
        id: 'whereWantToGo',
        answer: '',
        prompt: false,
        systemQuestion: true,
        surpriseMeText: true,
    }, // 0
    {
        text: getChatQuestions({ T, index: gptFilters?.departure ? 5 : 1, from: gptFilters?.departure }),
        time: new Date(),
        clientSender: false,
        id: 'fromAirport',
        answer: '',
        prompt: !!gptFilters?.departure,
        prefilledDeparture: gptFilters?.departure || '',
        systemQuestion: true,
    }, // 1
    {
        text: getChatQuestions({ T, index: gptFilters?.travelers ? 6 : 2, travelers: gptFilters?.travelers }),
        time: new Date(),
        clientSender: false,
        id: 'travelerCount',
        answer: '',
        prompt: !!gptFilters?.travelers,
        systemQuestion: true,
    }, // 2
    {
        text: getChatQuestions({ T, index: gptFilters?.infants ? 9 : 8, infants: gptFilters?.infants }),
        time: new Date(),
        clientSender: false,
        id: 'babyTravelerCount',
        answer: '',
        prompt: !!gptFilters?.infants,
        systemQuestion: true,
    }, // 3
    {
        // A leaveTime that has already passed is treated as if there were none at all:
        // ask question 3 with the date picker instead of the "still leaving on X?" prompt.
        text: getChatQuestions({
            T,
            index: hasUsableLeaveTime(gptFilters?.leaveTime) ? 10 : 3,
            leaveTime: hasUsableLeaveTime(gptFilters?.leaveTime) ? gptFilters?.leaveTime : '',
        }),
        time: new Date(),
        clientSender: false,
        id: 'whenLeave',
        answer: '',
        prompt: hasUsableLeaveTime(gptFilters?.leaveTime),
        datePicker: !hasUsableLeaveTime(gptFilters?.leaveTime),
        systemQuestion: true,
    }, // 4
    {
        text: getChatQuestions({ T, index: gptFilters?.nightCount ? 12 : 4, nightCount: gptFilters?.nightCount }),
        time: new Date(),
        clientSender: false,
        id: 'howManyNights',
        answer: '',
        prompt: !!gptFilters?.nightCount,
        systemQuestion: true,
    }, // 5
    {
        text: getChatQuestions({ T, index: 13 }),
        time: new Date(),
        clientSender: false,
        id: 'howMuchSpend',
        answer: '',
        priceRange: true,
        priceRangeFalse: false,
        systemQuestion: true,
    }, // 6
    {
        text: getChatQuestions({ T, index: 14 }),
        time: new Date(),
        id: 'greetAndHowTo',
        answer: '',
        systemQuestion: true,
    }, // 7
    {
        // Asked once, right after the greeting, and — like the greeting — not part of
        // the 0-6 walk: it carries no answer into the query, only the flag that decides
        // which query template is used at the end (see getQueryTranslationKey).
        text: getChatQuestions({ T, index: 16 }),
        time: new Date(),
        clientSender: false,
        id: 'packageOrStayOnly',
        answer: '',
        packageChoice: true,
        systemQuestion: true,
    }, // 8
]);

export const initializeChatScreen = (GPTChat, dispatch, setChats, offers, setActiveIndices, questionState, gptFilters, T, loginConfig) => {
    console.log("initializing chats");
    if (GPTChat.length === 0) {
        dispatch(setInitialChat(true));
        // const firstMessage = {
        //     ...initialQuestionState(T)[0],
        //     time: new Date(),
        //     isSurpriseQuestion: true,
        //     prompt: true,
        // };
        const firstMessage = {
            ...initialQuestionState(T, gptFilters)[7],
            systemQuestion: true,
            time: new Date(),
        };
        setChats([firstMessage]);
    } else {
        const uniqueOffers = offers.filter(
            (offer, index, self) =>
                index ===
                self.findIndex((o) => o.guidForBooking === offer.guidForBooking)
        );
        const result = uniqueOffers.reduce((acc, offer) => {
            acc[offer.guidForBooking] = 1;
            return acc;
        }, {});
        console.log({ result });
        setActiveIndices(result);
        setChats(GPTChat);
    }

    const _questions = [...questionState];
    if (gptFilters?.departure) {
        _questions[1] = {
            text: getChatQuestions({ T, index: 5, from: gptFilters?.departure }),
            time: new Date(),
            clientSender: false,
            id: "fromAirport",
            answer: "",
            prompt: true,
            prefilledDeparture: gptFilters?.departure || '',
            systemQuestion: true,
        };
    }
    if (gptFilters?.travelers) {
        _questions[2] = {
            text: getChatQuestions({ T, index: 6, travelers: gptFilters?.travelers }),
            time: new Date(),
            clientSender: false,
            id: "travelerCount",
            answer: "",
            prompt: true,
            systemQuestion: true,
        };
    }
    if (gptFilters?.infants) {
        _questions[3] = {
            text: getChatQuestions({ T, index: 9, infants: gptFilters?.infants }),
            time: new Date(),
            clientSender: false,
            id: "babyTravelerCount",
            answer: "",
            prompt: true,
            systemQuestion: true,
        };
    }
    if (gptFilters?.leaveTime) {
        // Normalize the date with the user's current locale
        const momentLocale = loginConfig?.locale?.code ? loginConfig.locale.code.split('_')[0].toLowerCase() : 'en';
        const parsedDate = parseLeaveTime(gptFilters.leaveTime, momentLocale);

        if (isLeaveTimeInPast(gptFilters.leaveTime, momentLocale)) {
            // The stored date has already passed, so there is nothing to confirm — ask for a
            // fresh one with the date picker, exactly as the "No" answer would.
            _questions[4] = {
                ...initialQuestionState(T)[4],
                time: new Date(),
            };
        } else {
            const normalizedLeaveTime = parsedDate
                ? parsedDate.locale(momentLocale).format("DD MMMM YYYY")
                : gptFilters.leaveTime; // Fallback to original if parsing fails

            _questions[4] = {
                text: getChatQuestions({ T, index: 10, leaveTime: normalizedLeaveTime }),
                time: new Date(),
                clientSender: false,
                id: "whenLeave",
                answer: "",
                prompt: true,
                systemQuestion: true,
            };
        }
    }
    if (gptFilters?.nightCount) {
        _questions[5] = {
            text: getChatQuestions({ T, index: 12, nightCount: gptFilters?.nightCount }),
            time: new Date(),
            clientSender: false,
            id: "howManyNights",
            answer: "",
            prompt: true,
            systemQuestion: true,
        };
    }

    console.log({ _questions });
    dispatch(updateQuestionState(_questions));
};

export const isOffersMessage = (item) =>
    !item?.clientSender &&
    item?.messageData?.searchGUID &&
    item?.messageData?.metaData;

// A hotel-only search comes back shaped like a package search, but with an empty
// searchGUID: there is no listing to fetch the tiles from yet, so only the
// deeplinks for the hotel list and map are usable.
export const isHotelOnlyMessage = (item) =>
    !item?.clientSender &&
    !item?.messageData?.searchGUID &&
    !!item?.messageData?.metaData &&
    !!(item?.messageData?.deeplinkList || item?.messageData?.deeplinkMap);

// An offers bubble from a destination-less (Surprise me) search, matching the Dest values
// getOffers turns into "[ALL][000001][Anywhere]". Read from the bubble's own metaData, so
// older carousels in the same chat keep their own answer when a newer search differs.
export const isAnywhereOffersMessage = (item) => {
    const metaData = item?.messageData?.metaData;
    if (!metaData) return false;
    const dest = queryStringToObject(metaData)?.Dest;
    return dest === "XXX" || dest === "Anywhere";
};

export const queryStringToObject = (queryString) => {
    // Split the query string into key-value pairs
    let pairs = queryString.split("&");

    // Create an object to store the key-value pairs
    let result = {};

    // Iterate through each key-value pair
    for (let i = 0; i < pairs.length; i++) {
        let pair = pairs[i].split("=");
        let key = decodeURIComponent(pair[0]);
        let value = decodeURIComponent(pair[1]);

        // Store the key-value pair in the object
        result[key] = value;
    }

    return result;
}

// The query sentence and the user's extra wishes travel as one message, but they
// must read as two paragraphs:
//
//   Genius will find a great package for you for 2 adults ... and all nights stay.
//
//   Additional wish(s): I want a swimming pool

export const withAdditionalRequestText = (T, baseText, additionalRequestText) => {
    const wishes = additionalRequestText?.trim();

    if (!wishes) return baseText;

    const label = T("Chat:Extra_Wish_Text");

    return `${baseText}\n\n${label}: ${wishes}`;
};

// Same caps the Travelers quick filter enforces.
export const MAX_TRAVELERS = 6;
export const MAX_TRAVELER_ROOMS = 3;

// Passenger counts arrive in metaData either unindexed (`ADT`, `CHD`, `INF`) for
// single-room searches, or room-indexed (`ADT1`, `CHD1`, `ADT2`, ...) once
// multiple rooms are requested.
const PASSENGER_KEY_PATTERN = /^(ADT|CHD|INF)(\d*)$/;

const roomTravelersCount = (room) => room.adult + room.child + room.infants;

/**
 * Reads the passenger counts out of a chat metaData query string (or an already
 * parsed metaData object) and returns them grouped per room, in room order,
 * capped to MAX_TRAVELER_ROOMS / MAX_TRAVELERS, alongside the totals.
 *
 * Every consumer of these counts — the hotel search, the travelers state and the
 * follow-up flight requests built from it — must go through here, so they can
 * never disagree about who is travelling.
 */
export const parsePassengersFromMetaData = (metaData) => {
    const metaDataObj =
        typeof metaData === "string" ? queryStringToObject(metaData) : metaData || {};

    const roomsByNumber = new Map();

    Object.keys(metaDataObj).forEach((key) => {
        const match = PASSENGER_KEY_PATTERN.exec(key.trim());
        if (!match) return;

        const [, category, roomIndex] = match;
        // parseInt also copes with values carrying a trailing "|..." section.
        const count = parseInt(metaDataObj[key]) || 0;
        if (!count) return;

        // Unindexed keys belong to the only room of a single-room search.
        const roomNumber = roomIndex ? parseInt(roomIndex) : 1;
        const room = roomsByNumber.get(roomNumber) || {
            room: roomNumber,
            adult: 0,
            child: 0,
            infants: 0,
        };

        if (category === "ADT") room.adult += count;
        if (category === "CHD") room.child += count;
        if (category === "INF") room.infants += count;

        roomsByNumber.set(roomNumber, room);
    });

    let travelersTotal = 0;
    const rooms = [...roomsByNumber.values()]
        .sort((a, b) => a.room - b.room)
        .slice(0, MAX_TRAVELER_ROOMS)
        .filter((room) => {
            if (travelersTotal + roomTravelersCount(room) > MAX_TRAVELERS) return false;
            travelersTotal += roomTravelersCount(room);
            return true;
        });

    return {
        rooms,
        adults: rooms.reduce((n, r) => n + r.adult, 0),
        children: rooms.reduce((n, r) => n + r.child, 0),
        infants: rooms.reduce((n, r) => n + r.infants, 0),
    };
};

// The opening bubble asks whether the trip is a complete travel package or a stay only.
// Both answers run through the very same guided flow; the only thing the choice changes
// is which sentence template the collected answers are poured into at the end. Both
// templates take the identical placeholder set, so callers pass the same params object.
export const getQueryTranslationKey = (stayOnly = false) =>
    stayOnly ? "Chat:Chat_Bot_Query_Stay_Only_Input_For_Api" : "Chat:Chat_Bot_Query_Input_For_Api";

// The Stay only bubble drops the "in and around <destination>" part of the sentence;
// the destination still goes to the API through the template above.
const STAY_ONLY_DISPLAY_KEY = "Chat:Chat_Bot_Query_Stay_Only_Display";

// A destination-less (Surprise me) search: the word "Anywhere" in any case, or the
// "[ALL][<id>][Anywhere]" code a saved search carries.
const ANYWHERE_CODE_PATTERN = /^\[ALL\]\[\d+\]\[Anywhere\]$/i;

export const isAnywhereDestination = (to) => {
    if (typeof to !== "string") return false;
    const value = to.trim();
    return value.toLowerCase() === "anywhere" || ANYWHERE_CODE_PATTERN.test(value);
};

// The sentence shown in the chat bubble. T returns "" for a missing key, so the bubble
// falls back to the full API sentence until the display key is translated.
// An Anywhere destination is worded by the CMS here only; the API sentence keeps "Anywhere".
export const getQueryDisplayText = (T, stayOnly, params) => {
    const apiKey = getQueryTranslationKey(stayOnly);
    const displayParams = isAnywhereDestination(params?.to)
        ? { ...params, to: T("Chat:Chat_Bot_Anywhere_Replacement") || params.to }
        : params;
    return stayOnly
        ? T(STAY_ONLY_DISPLAY_KEY, displayParams) || T(apiKey, displayParams)
        : T(apiKey, displayParams);
};

export const getMessageQuery = (T, gptFilters, questions, inputText, price, currency, stayOnly = false) => {
    const getValue = (id) => questions.find((q) => q.id === id).answer;
    if (gptFilters?.initialChat) {
        const params = {
            travelers: getValue("travelerCount"),
            infants: getValue("babyTravelerCount"),
            to:
                inputText &&
                    getValue("whereWantToGo") === T("Chat:Chat_Bot_Surprise_Me_Api")
                    ? T("Chat:Chat_Bot_Surprise_Me_Input")
                    : getValue("whereWantToGo"),
            leaveTime: getValue("whenLeave"),
            nightsCount: getValue("howManyNights"),
            from: getValue("fromAirport"),
            low: `${currency}${price?.low}`,
            high: `${currency}${price?.high}`,
        };
        // `inputText` marks the display call.
        return inputText
            ? getQueryDisplayText(T, stayOnly, params)
            : T(getQueryTranslationKey(stayOnly), params);
    } else {
        return "";
    }
};

// Stay-only replies never reach getOffers (no listing call), so their recent-search
// entry is built here from the reply's metaData, in the same shape getOffers saves —
// departure included, since the search-only listing API needs it. Only the chip hides
// the departure (formatFlightSummary).
export const buildStayOnlyLastSearch = (metaData, price, chatGPTPrice, additionalRequestText = "") => {
    const metaDataObj = queryStringToObject(metaData || "");
    const dest = metaDataObj?.Dest === "XXX" || metaDataObj?.Dest === "Anywhere"
        ? "[ALL][000001][Anywhere]"
        : metaDataObj?.Dest;

    // Same band getOffers would search with: the assistant's own ceiling wins.
    const hasGPTPrice = Number.isFinite(chatGPTPrice?.high) && chatGPTPrice.high > 0;
    const FilterPrePost = hasGPTPrice
        ? {
            minPrice: Number.isFinite(chatGPTPrice?.low) && chatGPTPrice.low >= 0 ? chatGPTPrice.low : 0,
            maxPrice: Number(chatGPTPrice.high),
        }
        : { minPrice: Number(price?.low), maxPrice: Number(price?.high) };

    const PassengerAgeRoomItemList = [];
    parsePassengersFromMetaData(metaDataObj).rooms.forEach((room, index) => {
        const Room = index + 1;
        for (let i = 0; i < room.infants; i++) PassengerAgeRoomItemList.push({ PassengerCategory: "Infant", Room });
        for (let i = 0; i < room.adult; i++) PassengerAgeRoomItemList.push({ PassengerCategory: "Adult", Room });
        for (let i = 0; i < room.child; i++) PassengerAgeRoomItemList.push({ PassengerCategory: "Child", Room });
    });

    return {
        FilterPrePost,
        FlightSegmentList: [
            { Departure: { CodeList: [metaDataObj?.Dep], Date: metaDataObj?.DepDate }, Arrival: { CodeList: [dest] } },
            { Departure: { CodeList: [dest], Date: metaDataObj?.RetDate }, Arrival: { IataCodeList: [metaDataObj?.Dep] } },
        ],
        PassengerAgeRoomItemList,
        date: new Date(),
        GUID: "",
        id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
        ...(additionalRequestText ? { additionalRequest: additionalRequestText } : {}),
        stayOnly: true,
    };
};

export const getOffers = (
    itemMessage,
    exploreMore,
    appConfig,
    loginConfig,
    price,
    setIsSent,
    dispatch,
    setActiveIndices,
    T,
    setOffersFetched,
    activeIndices,
    chatContainerRef,
    onOffersReady,
    setPriceHandler,
    handleNoCaseWithMethodForFlight,
    chatGPTPrice,
    handlePriceUpdateChatWizard,
    removeChatMessageBySearchGUID,
    additionalRequestText = "",
    stayOnly = false
) => {
    const metaDataObj = queryStringToObject(itemMessage?.messageData?.metaData);
    // The same ceiling the chat's slider is drawn with, so the 204.02 retry and the
    // post-search slider realignment land on the band this product actually allows —
    // never the other product's, in either direction.
    const priceCeiling = stayOnly ? AppConfig.maxPriceStayOnly : AppConfig.maxPrice;

    try {
        let searchParams = {
            transaction_id: appConfig?.transaction_id,
            GUID: itemMessage?.messageData?.searchGUID,
            Locale: loginConfig?.locale?.code,
            Page: 0,
            currency: loginConfig?.currency?.code,
            SearchType: "GPT",
            FilterPrePost: {
                minPrice: Number(price?.low),
                maxPrice: Number(price?.high),
            },
        };

        // A pseudocode block only overrides the slider when it carries a usable
        // ceiling. `low` is optional and means "no minimum", so it falls back to 0 —
        // spelled out here because the previous `chatGPTPrice?.low >= 0` test passed
        // for a missing (null) value by coercion, which read as deliberate but wasn't.
        if (Number.isFinite(chatGPTPrice?.high) && chatGPTPrice.high > 0) {
            const gptMinPrice = Number.isFinite(chatGPTPrice?.low) && chatGPTPrice.low >= 0
                ? chatGPTPrice.low
                : 0;
            searchParams.FilterPrePost.minPrice = gptMinPrice;
            searchParams.FilterPrePost.maxPrice = Number(chatGPTPrice.high);
        }

        let PassengerAgeRoomItemList = [];
        // Rooms are numbered sequentially in the request, independent of the
        // index used by the metaData keys.
        parsePassengersFromMetaData(metaDataObj).rooms.forEach((room, index) => {
            const roomCounter = index + 1;
            for (let i = 0; i < room.infants; i++) {
                PassengerAgeRoomItemList.push({
                    PassengerCategory: "Infant",
                    Room: roomCounter,
                });
            }
            for (let i = 0; i < room.adult; i++) {
                PassengerAgeRoomItemList.push({
                    PassengerCategory: "Adult",
                    Room: roomCounter,
                });
            }
            for (let i = 0; i < room.child; i++) {
                PassengerAgeRoomItemList.push({
                    PassengerCategory: "Child",
                    Room: roomCounter,
                });
            }
        });

        let FlightSegmentList = [];

        let inbound_params = {};
        let outbound_params = {};
        inbound_params.Departure = {
            CodeList: [metaDataObj?.Dep],
            Date: metaDataObj?.DepDate,
        };

        inbound_params.Arrival = {
            CodeList: [
                metaDataObj?.Dest === "XXX" || metaDataObj?.Dest === "Anywhere"
                    ? "[ALL][000001][Anywhere]"
                    : metaDataObj?.Dest,
            ],
        };

        outbound_params.Departure = {
            CodeList: [
                metaDataObj?.Dest === "XXX" || metaDataObj?.Dest === "Anywhere"
                    ? "[ALL][000001][Anywhere]"
                    : metaDataObj?.Dest,
            ],
            Date: metaDataObj?.RetDate,
        };

        outbound_params.Arrival = {
            IataCodeList: [metaDataObj?.Dep],
        };

        FlightSegmentList.push(inbound_params);
        FlightSegmentList.push(outbound_params);

        if (FlightSegmentList.length > 0) {
            searchParams.FlightSegmentList = FlightSegmentList;
        }

        if (PassengerAgeRoomItemList.length > 0) {
            searchParams.PassengerAgeRoomItemList = PassengerAgeRoomItemList;
        }

        const executeSearch = async (params) => {
            try {
                const response = await GetSearchOnlyHotelsService(params);
                console.log({ response });
                let preventRemoveChatMessage = false;
                setIsSent(true);
                // setInputPlaceholderText(T("Chat:Chat_Bot_Input_Placeholder"));
                dispatch(setInitialChat(false));

                if (response?.hotelsArray?.length && onOffersReady) {
                    onOffersReady(itemMessage?.time);
                }

                if (response?.response) {
                    if (
                        response?.response?.data?.warnings?.length &&
                        response?.response?.data?.warnings[0]?.code === "204.02"
                    ) {
                        const result = await Swal.fire({
                            title: T("Common:No_Matches_Under_Budget"),
                            // text: T("Common:No_Matches_Under_Budget_Description"), // 'We couldn’t find any direct flights. Would you like to see results with a stopover?',
                            text: response?.response?.data?.warnings[0]?.description || T("Common:No_Matches_Under_Budget_Description"), // 'We couldn’t find any direct flights. Would you like to see results with a stopover?',
                            iconHtml: `
                                <img src=${GeniusImage}
                                     alt="info"
                                     style="width:80px;" />
                              `,
                            showCancelButton: true,
                            confirmButtonText: T("Buttons:Button_Yes"),
                            cancelButtonText: T("Buttons:Button_No"),
                            confirmButtonColor: "var(--green)",
                            cancelButtonColor: "#596C7F",
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                        });
                        // IF USER CLICKS YES → retry with full app price range
                        if (result.isConfirmed) {
                            const retryParams = {
                                ...params,
                                FilterPrePost: {
                                    minPrice: AppConfig.minPrice,
                                    maxPrice: priceCeiling,
                                },
                                SearchType: "Popup",
                            };
                            dispatch(setPriceRangeValue({ low: AppConfig.minPrice, high: priceCeiling }));
                            setPriceHandler({ low: AppConfig.minPrice, high: priceCeiling });
                            dispatch(
                                setPriceExceedOffersByGuid({
                                    guid: itemMessage?.messageData?.searchGUID,
                                    status: true,
                                })
                            );
                            setIsSent(false);
                            // setInputPlaceholderText(T("Chat:Chat_Bot_Waiting_Placeholder_Text"));
                            return executeSearch(retryParams);
                        } else {
                            // setInputPlaceholderText(T("Chat:Chat_Bot_Price_Exceed_Placeholder"));
                            if (typeof handleNoCaseWithMethodForFlight === "function") {
                                removeChatMessageBySearchGUID(itemMessage?.messageData?.searchGUID);
                                handleNoCaseWithMethodForFlight(T("Chat:Chat_Bot_Price_Exceed_Placeholder"));
                                preventRemoveChatMessage = true;
                            }
                        }
                    }

                    if (
                        response?.response?.data?.warnings?.length &&
                        response?.response?.data?.warnings[0]?.code === "204.05"
                    ) {
                        // toast.info(response?.response?.data?.warnings[0].description);
                        const result = await Swal.fire({
                            title: T("Common:Flights_Info"),
                            text: T("Common:Continue_With_Direct_Flights"), // 'We couldn’t find any direct flights. Would you like to see results with a stopover?',
                            iconHtml: `
                                <img src=${GeniusImage}
                                     alt="info"
                                     style="width:80px;" />
                              `,
                            showCancelButton: true,
                            confirmButtonText: T("Buttons:Button_Yes"),
                            cancelButtonText: T("Buttons:Button_No"),
                            confirmButtonColor: "var(--green)",
                            cancelButtonColor: "#596C7F",
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                        });
                        // IF USER CLICKS NO → STOP EXECUTION
                        if (!result.isConfirmed) {
                            // setInputPlaceholderText(T("Chat:Chat_Bot_Flight_Modal_Stopover_Placeholder"));
                            if (typeof handleNoCaseWithMethodForFlight === "function") {
                                removeChatMessageBySearchGUID(itemMessage?.messageData?.searchGUID);
                                handleNoCaseWithMethodForFlight(T("Chat:Chat_Bot_Flight_Modal_Stopover_Placeholder"));
                            }
                            return;
                        } else {
                            preventRemoveChatMessage = true;
                        }
                    }

                    if (
                        response?.response?.data?.warnings?.length &&
                        response?.response?.data?.warnings[0]?.code === "204.03"
                    ) {
                        await Swal.fire({
                            title: T("Common:Offers_Not_Found"),
                            text: response?.response?.data?.warnings[0]?.description,
                            iconHtml: `
                                <img src=${GeniusImage}
                                     alt="info"
                                     style="width:80px;" />
                              `,
                            showCancelButton: false,
                            confirmButtonText: T("Buttons:Button_Ok"),
                            confirmButtonColor: "var(--green)",
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                        });
                    }
                }

                // Nothing to show for this search — drop the chat message it belongs to.
                const hasNoOffers = response?.hotelsArray?.length === 0;
                const hasWarnings = response?.response?.data?.warnings?.length > 0;

                if (
                    (hasNoOffers || hasWarnings) &&
                    !preventRemoveChatMessage &&
                    typeof removeChatMessageBySearchGUID === "function"
                ) {
                    removeChatMessageBySearchGUID(itemMessage?.messageData?.searchGUID);
                }

                console.log({ exploreMore }, response?.hotelsArray?.length);

                if (exploreMore && response?.hotelsArray?.length) {
                    dispatch(setIsChatGPTOffers(true));
                    dispatch(setListingGuid(response?.correlationId));
                    dispatch(setOffers(response?.hotelsArray));
                    dispatch(setActivityIndicator(false));
                } else if (!exploreMore && response?.hotelsArray?.length) {
                    const updatedIndices = { ...activeIndices };
                    updatedIndices[itemMessage?.messageData?.searchGUID] = 1;
                    setActiveIndices(updatedIndices);
                    dispatch(setChatGPTOffers(response?.hotelsArray)); // adding offers for search
                }

                if (chatContainerRef?.current?.scrollHeight) {
                    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;

                    // Retry scroll multiple times to ensure carousel fully renders
                    setTimeout(() => {
                        if (chatContainerRef?.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        }
                    }, 150);

                    setTimeout(() => {
                        if (chatContainerRef?.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        }
                    }, 400);

                    setTimeout(() => {
                        if (chatContainerRef?.current) {
                            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                        }
                    }, 700);
                }

                if (response?.response?.data?.metaData?.metaTravelData) {
                    const metaTravelData =
                        response?.response?.data?.metaData?.metaTravelData;


                    dispatch(setTravelDate({
                        startDate: moment(new Date(metaTravelData?.depDate)).format('ddd MMM DD YYYY'),
                        endDate: moment(new Date(metaTravelData?.retDate)).format('ddd MMM DD YYYY'),
                    }));

                    // Format the date with the user's locale
                    const momentLocale = loginConfig?.locale?.code ? loginConfig.locale.code.split('_')[0].toLowerCase() : 'en';
                    dispatch(
                        setGPTLeaveTime(
                            moment(metaTravelData?.depDate).locale(momentLocale).format("DD MMMM YYYY")
                        )
                    ); // Setting departure date from meta data in GeniusGPT

                    await getAirportFullObject({
                        text: metaTravelData?.from,
                        locale: loginConfig?.locale?.code,
                        dispatch,
                    }); // Setting departure from for GeniusGPT.

                    await getDesireDestinationFullObject({
                        text: metaTravelData?.to,
                        locale: loginConfig?.locale?.code,
                        dispatch,
                    }); // Setting desired country from metaData in app local storage.

                    dispatch(setGPTDeparture(metaTravelData?.from)); // Setting departure from for GeniusGPT.

                    let count = nightDifferenceCountForFlightsDate(
                        metaTravelData?.depDate,
                        metaTravelData?.retDate
                    );
                    dispatch(setGPTNightCount(count.toString()));

                    dispatch(
                        setStarRating({
                            min: metaDataObj?.MinStar,
                            max: metaDataObj?.MaxStar,
                        })
                    ); // Storing the Star Rating

                    dispatch(
                        setMetaDataCount(
                            response?.response?.data?.metaData?.offersAfterFilter || 0
                        )
                    );
                    dispatch(
                        setMetaDataCountByGuid({
                            guid: itemMessage?.messageData?.searchGUID,
                            count: response?.response?.data?.metaData?.offersAfterFilter || 0,
                        })
                    );


                    dispatch(setMetaData(response?.response?.data?.metaData));

                    // Update the price range in the chat wizard with respect to the search results
                    handlePriceUpdateChatWizard({
                        low: params?.FilterPrePost?.minPrice,
                        high: params?.FilterPrePost?.maxPrice <= priceCeiling
                            ? params?.FilterPrePost?.maxPrice
                            : priceCeiling,
                    });
                }

                if (response?.hotelsArray?.length) {
                    // `params` is the payload the search API received; the extra wish is
                    // deliberately kept out of it and attached only to the cached entry,
                    // so the history chip can replay it later.
                    // `stayOnly` rides along for the same reason: the search payload is
                    // identical for both products, so without it a replayed chip could not
                    // tell which query template built it and always read back as a package.
                    // Re-searching the same route still collapses onto one chip (setLastSearch
                    // matches on the flight code lists), and the newest search owns the flag.
                    const lastSearchParams = {
                        ...params,
                        date: new Date(),
                        GUID: '',
                        id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
                        ...(additionalRequestText ? { additionalRequest: additionalRequestText } : {}),
                        ...(stayOnly ? { stayOnly: true } : {}),
                    };

                    dispatch(setLastSearch(lastSearchParams)); // storing last search as cache.
                }

                setOffersFetched(true);
            } catch (error) {
                console.log({ error });
                setIsSent(true);
                // setInputPlaceholderText(T("Chat:Chat_Bot_Input_Placeholder"));

                if (error?.response?.data?.errors?.length) {
                    toast.error(error?.response?.data?.errors[0]?.description);
                } else if (error?.response?.status === 500) {
                    toast.error(T("Toasts:Toast_Description_Server_Error_Message"));
                }
            }
        };

        setIsSent(false);
        // setInputPlaceholderText(T("Chat:Chat_Bot_Waiting_Placeholder_Text"));
        // console.log({searchParams});
        console.log(JSON.stringify(searchParams), "Chat page params");

        executeSearch(searchParams);
    } catch (e) {
        console.log(JSON.stringify(e));
        setIsSent(true);
        // setInputPlaceholderText(T("Chat:Chat_Bot_Input_Placeholder"));
    }
};

// `chatGPTOffers` accumulates every search of the session, so the guid is what
// isolates one message's results. It is deliberately the ONLY filter: the price
// band was already applied server-side via `FilterPrePost` when this guid was
// searched, and re-applying the chat's live `price` here re-filtered *previously
// rendered* carousels every time a new search (or a history chip) moved it.
export const getCustomOffers = (item, offers) => {
    const searchGUID = item?.messageData?.searchGUID;
    if (!searchGUID) return [];

    return offers
        .filter((offer) => offer.guidForBooking === searchGUID)
        .slice(0, 3);
};

export const handleNavigationToMoreOffers = async (type, dispatch, item, messageItem, loginConfig, navigate, metaDataCountByGuid, priceExceedOffersByGuid) => {
    // When the GeniusChatGPT response provides an explicit deeplink for this button, use it directly and skip the computed navigation.
    const deeplink = type === 'search' ? messageItem?.messageData?.deeplinkList : messageItem?.messageData?.deeplinkMap;
    if (deeplink) {
        window.open(deeplink, '_blank', 'noopener,noreferrer');
        return;
    }

    dispatch(setActivityIndicator(true));
    // type can be either 'search' or 'map', depending on where the user wants to navigate to see more offers.
    console.log({ item }, { messageItem });

    const metaData = messageItem?.messageData?.metaData;
    const params = new URLSearchParams(metaData);
    const from = params.get("Dep");
    const to = params.get("Dest");

    const obj = Object.fromEntries(params.entries());

    console.log({ obj });

    if (obj?.DepDate && obj?.RetDate) {
        dispatch(
            setTravelDate({
                startDate: moment(new Date(obj?.DepDate)).format('ddd MMM DD YYYY'),
                endDate: moment(new Date(obj?.RetDate)).format('ddd MMM DD YYYY'),
            })
        ); // Setting dates from mete data in app local storage.
    }

    if (from) {
        await getAirportFullObject({
            text: from,
            locale: loginConfig?.locale?.code,
            dispatch,
        }); // Setting departure from for GeniusGPT.
    }

    if (to) {
        await getDesireDestinationFullObject({
            text: to,
            locale: loginConfig?.locale?.code,
            dispatch,
        }); // Setting desired country from metaData in app local storage.
    }

    dispatch(setActivityIndicator(false));

    const guid = messageItem?.messageData?.searchGUID;
    const totalCount = metaDataCountByGuid?.[guid] || 0;
    const isPriceExceedOffers = priceExceedOffersByGuid?.[guid] || false;
    const middlePage = isPriceExceedOffers ? 1 : getPagerMiddlePage(totalCount, 10) || 1;
    if (type === 'search') {
        navigate(
            "/search/" +
            guid +
            "/query?page=" +
            middlePage +
            "&locale=" +
            loginConfig.locale.code +
            "&currency=" +
            loginConfig?.currency?.code +
            "&searchType=Listing"
        );
    } else {
        navigate(`/map/${guid}/offers/chat/${middlePage}`);
    }
}

export const getMessageText = (item) => {
    if (isOffersMessage(item)) {
        const pattern = /\[PSEUDOCODE\][\s\S]*?\[\/PSEUDOCODE\]/g;
        const cleanedText = item?.text?.replace(pattern, "");
        return cleanedText;
    }
    return item?.text;
};

export const extractSearchFiltersFromHistory = (searchObject) => {
    if (!searchObject) return null;

    try {
        const flightSegments = searchObject?.FlightSegmentList || [];
        const passengerList = searchObject?.PassengerAgeRoomItemList || [];
        const filterPrePost = searchObject?.FilterPrePost || {};

        // The chat only models two groups: "travelers" (everyone holding a seat) and
        // "infants" (lap babies). A stored search carries three categories, so children
        // count as travelers. Reading them as infants — and ignoring the Infant entries
        // entirely — silently dropped real infants when replaying a search.
        const countCategory = (category) =>
            passengerList.filter(p => p?.PassengerCategory === category).length;
        const travelers = countCategory("Adult") + countCategory("Child");
        const infants = countCategory("Infant");

        // Extract departure and arrival from first segment
        const firstSegment = flightSegments[0] || {};
        const secondSegment = flightSegments[1] || {};

        const from = firstSegment?.Departure?.CodeList?.[0] || "";
        const to = firstSegment?.Arrival?.CodeList?.[0] || firstSegment?.Arrival?.IataCodeList?.[0] || "";
        const leaveTime = firstSegment?.Departure?.Date || "";

        // Calculate nights count (difference between return and departure dates)
        const departureDate = new Date(firstSegment?.Departure?.Date);
        const returnDate = new Date(secondSegment?.Departure?.Date);
        const nightsCount = Math.floor((returnDate - departureDate) / (1000 * 60 * 60 * 24));

        return {
            travelers: travelers.toString(),
            infants: infants.toString(),
            to,
            from,
            leaveTime,
            nightsCount: nightsCount.toString(),
            low: filterPrePost?.minPrice || 0,
            high: filterPrePost?.maxPrice || 2500,
        };
    } catch (error) {
        console.error("Error extracting filters from search history:", error);
        return null;
    }
};
